import { useState } from "react";
import { GiStarsStack, GiTrophyCup, GiCheckMark, GiLockedChest } from "react-icons/gi";
import { useCastle } from "../api/castle";
import { useCreateInvoice } from "../api/payments";
import {
  BattlePassDef,
  ActiveBattlePass,
  isDayClaimed,
  useBattlePassCatalog,
  useClaimBattlePassDay,
  useMyBattlePasses,
} from "../api/battlePass";
import { Loading } from "./Loading";
import { ErrorView } from "./ErrorView";
import { ResourceBar } from "../components/ResourceBar";
import { ScreenBanner } from "../components/ScreenBanner";
import { formatNumber } from "../lib/format";
import { hapticImpact, hapticNotification, openInvoice, showAlert } from "../lib/telegram";

interface Props {
  embedded?: boolean;
}

// Battle Pass screen. Two modes:
//   1. No active pass — list buyable presets with full reward grid preview.
//   2. Active pass — show countdown, current day, 30-tile claim grid,
//      and the buy option below for stacking / when current expires.
export function BattlePassScreen({ embedded = false }: Props = {}) {
  const castle = useCastle();
  const catalog = useBattlePassCatalog();
  const mine = useMyBattlePasses();
  const createInvoice = useCreateInvoice();
  const claim = useClaimBattlePassDay();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [claimingKey, setClaimingKey] = useState<string | null>(null);

  if (castle.isPending || catalog.isPending || mine.isPending) {
    return <Loading message="Открываю боевой пропуск..." />;
  }
  if (castle.isError || catalog.isError || mine.isError) {
    return <ErrorView message="Не удалось загрузить боевой пропуск." />;
  }
  const data = castle.data!;
  const passes = catalog.data!;
  const active = mine.data!;

  const onBuy = async (def: BattlePassDef) => {
    hapticImpact("medium");
    setPendingId(def.id);
    try {
      const invoice = await createInvoice.mutateAsync(def.id);
      const status = await openInvoice(invoice.invoiceUrl);
      if (status === "paid") {
        hapticNotification("success");
        setTimeout(() => {
          mine.refetch();
          castle.refetch();
        }, 1500);
        await showAlert(`${def.title} активирован. Награды доступны ежедневно.`);
      } else if (status === "cancelled") {
        hapticNotification("warning");
      } else if (status === "failed") {
        hapticNotification("error");
        await showAlert("Оплата не прошла. Попробуй ещё раз.");
      }
    } catch (err) {
      hapticNotification("error");
      await showAlert("Не получилось создать счёт. Попробуй позже.");
    } finally {
      setPendingId(null);
    }
  };

  const onClaim = async (passId: string, day: number) => {
    const key = `${passId}:${day}`;
    hapticImpact("light");
    setClaimingKey(key);
    try {
      await claim.mutateAsync({ passId, day });
      hapticNotification("success");
    } catch (err) {
      hapticNotification("error");
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        await showAlert("Награда за этот день уже забрана или ещё не открыта.");
      } else {
        await showAlert("Не получилось забрать награду. Попробуй ещё раз.");
      }
    } finally {
      setClaimingKey(null);
    }
  };

  const body = (
    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
      {active.length === 0 ? (
        <EmptyState />
      ) : (
        active.map((a) => {
          const def = passes.find((p) => p.id === a.passId);
          if (!def) return null;
          return (
            <ActivePassCard
              key={a.rowId}
              active={a}
              def={def}
              claimingKey={claimingKey}
              onClaim={(day) => onClaim(a.passId, day)}
            />
          );
        })
      )}

      <div className="rounded-2xl bg-tg-secondaryBg/80 p-3">
        <h3 className="mb-2 text-sm font-semibold text-tg-hint">
          {active.length === 0 ? "Доступно к покупке" : "Ещё пропуски"}
        </h3>
        <div className="space-y-3">
          {passes.map((def) => {
            const alreadyActive = active.some((a) => a.passId === def.id);
            return (
              <PassOfferCard
                key={def.id}
                def={def}
                disabled={alreadyActive}
                pending={pendingId === def.id}
                onBuy={() => onBuy(def)}
              />
            );
          })}
        </div>
      </div>

      <div className="rounded-xl bg-tg-secondaryBg/60 p-3 text-xs leading-relaxed text-tg-hint">
        Пропуск активируется сразу после оплаты. Награды открываются по
        одному дню: бит «забрано» ставится в момент клика и больше не
        сбрасывается. Просроченные награды сгорают вместе с пропуском.
      </div>
    </div>
  );

  if (embedded) return body;

  return (
    <div className="flex h-full flex-col">
      <ScreenBanner kind="profile" title="Боевой пропуск" subtitle="Ежедневные награды" />
      <ResourceBar resources={data.state.resources} safeStorage={data.safeStorage} />
      {body}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-tg-hint/30 p-6 text-center">
      <GiTrophyCup className="mx-auto h-10 w-10 text-tg-hint" />
      <p className="mt-2 text-sm text-tg-hint">
        Активного пропуска нет. Купи Мини- или Сезонный, чтобы получать
        ежедневные награды.
      </p>
    </div>
  );
}

function ActivePassCard({
  active,
  def,
  claimingKey,
  onClaim,
}: {
  active: ActiveBattlePass;
  def: BattlePassDef;
  claimingKey: string | null;
  onClaim: (day: number) => void;
}) {
  const daysRemaining = Math.max(0, Math.ceil(active.secondsRemaining / 86_400));
  return (
    <div className="rounded-2xl bg-tg-secondaryBg p-3 shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold">{def.title}</h3>
          <p className="mt-0.5 text-xs text-tg-hint">
            День {active.currentDay} из {def.durationDays} · осталось {daysRemaining}{" "}
            {pluralDays(daysRemaining)}
          </p>
        </div>
        <div className="text-right text-xs text-tg-hint">
          {Math.round((active.currentDay / def.durationDays) * 100)}%
        </div>
      </div>

      <div className="mt-3 grid grid-cols-5 gap-1.5">
        {def.schedule.map((d) => {
          const claimed = isDayClaimed(active.claimedDaysMask, d.day);
          const isCurrent = d.day === active.currentDay;
          const isFuture = d.day > active.currentDay;
          const claimable = !claimed && !isFuture;
          const isClaiming = claimingKey === `${def.id}:${d.day}`;
          return (
            <DayTile
              key={d.day}
              day={d.day}
              reward={d.reward}
              claimed={claimed}
              isCurrent={isCurrent}
              claimable={claimable}
              isClaiming={isClaiming}
              onClick={claimable ? () => onClaim(d.day) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

function DayTile({
  day,
  reward,
  claimed,
  isCurrent,
  claimable,
  isClaiming,
  onClick,
}: {
  day: number;
  reward: { resources: { gold: number; wood: number; stone: number; mana: number }; inventoryItems?: Record<string, number> | null; soulShards: number; universalShards?: Record<string, number> | null };
  claimed: boolean;
  isCurrent: boolean;
  claimable: boolean;
  isClaiming: boolean;
  onClick?: () => void;
}) {
  const bg = claimed
    ? "bg-emerald-700/40 border-emerald-500/40"
    : isCurrent
      ? "bg-tg-accent/30 border-tg-accent ring-1 ring-tg-accent/60"
      : claimable
        ? "bg-tg-secondaryBg border-tg-hint/30"
        : "bg-tg-secondaryBg/40 border-tg-hint/10 opacity-60";

  return (
    <button
      type="button"
      disabled={!claimable || isClaiming}
      onClick={onClick}
      className={`relative flex aspect-square flex-col items-center justify-center rounded-lg border p-1 text-[10px] ${bg}`}
    >
      <div className="absolute left-1 top-0.5 text-[9px] font-semibold text-tg-hint">{day}</div>
      {claimed ? (
        <GiCheckMark className="h-5 w-5 text-emerald-300" />
      ) : !claimable ? (
        <GiLockedChest className="h-4 w-4 text-tg-hint/60" />
      ) : (
        <RewardGlyph reward={reward} />
      )}
      {isClaiming && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-[10px]">
          ...
        </div>
      )}
    </button>
  );
}

function RewardGlyph({
  reward,
}: {
  reward: { resources: { gold: number; wood: number; stone: number; mana: number }; inventoryItems?: Record<string, number> | null; soulShards: number; universalShards?: Record<string, number> | null };
}) {
  if (reward.universalShards && Object.keys(reward.universalShards).length > 0) {
    return <span className="text-base">💎</span>;
  }
  if (reward.soulShards > 0) {
    return <span className="text-base">✦</span>;
  }
  if (reward.inventoryItems && Object.keys(reward.inventoryItems).length > 0) {
    return <span className="text-base">⏱</span>;
  }
  const total = reward.resources.gold + reward.resources.wood + reward.resources.stone + reward.resources.mana;
  if (total > 0) {
    return <span className="text-[9px]">{formatNumber(total)}</span>;
  }
  return <span className="text-base">?</span>;
}

function PassOfferCard({
  def,
  disabled,
  pending,
  onBuy,
}: {
  def: BattlePassDef;
  disabled: boolean;
  pending: boolean;
  onBuy: () => void;
}) {
  return (
    <div className="rounded-2xl bg-tg-bg/40 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold">{def.title}</h3>
            {def.highlight && (
              <span className="rounded-full bg-tg-accent/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-tg-accent">
                {def.highlight}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-tg-hint">{def.description}</p>
          <p className="mt-1 text-[11px] text-tg-hint">
            {def.durationDays} {pluralDays(def.durationDays)} · {def.schedule.length} наград
          </p>
        </div>
        <button
          type="button"
          disabled={disabled || pending}
          onClick={onBuy}
          className="flex shrink-0 items-center gap-1 rounded-lg bg-tg-button px-3 py-2 text-sm font-semibold text-tg-buttonText disabled:opacity-50"
        >
          <GiStarsStack className="h-4 w-4" />
          {def.starsPrice}
        </button>
      </div>
      {disabled && (
        <p className="mt-2 text-[11px] text-emerald-300">Уже активен</p>
      )}
    </div>
  );
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "дня";
  return "дней";
}
